import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { DojoMessage } from '@/types/dojo';
import type { DojoBeat } from '@/types/dojo';
import { prepareHowieMarkdown, renderHowieMarkdown } from '@/components/HowieMarkdown';
import { getChatContentScrollSignal } from '@lightning/utils';
import { cn } from '@/lib/utils';

interface UnitChatProps {
  messages: DojoMessage[];
  streamingText: string;
  isStreaming: boolean;
  isSending: boolean;
  error?: string | null;
  /** Rendered under the transcript — the insight capture or round-complete card. */
  footer?: ReactNode;
  /** Keep the active beat in view while making earlier dialogue available on demand. */
  collapseBeforeBeat?: DojoBeat;
  className?: string;
}

/**
 * The coaching column uses the same typographic and message treatment as the
 * primary Howie conversation so moving into practice does not change voices.
 */
export function UnitChat({
  messages,
  streamingText,
  isStreaming,
  isSending,
  error = null,
  footer,
  collapseBeforeBeat,
  className,
}: UnitChatProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showEarlier, setShowEarlier] = useState(false);
  const scrollSignal = useMemo(
    () => getChatContentScrollSignal(messages, streamingText),
    [messages, streamingText]
  );

  useEffect(() => {
    if (!scrollSignal) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [scrollSignal]);

  useEffect(() => {
    setShowEarlier(false);
  }, [collapseBeforeBeat]);

  let firstFocusedMessage = -1;
  if (collapseBeforeBeat) {
    // Open on the current coaching prompt, not the beginning of the current
    // beat. Restored legacy messages do not always carry a beat, so the latest
    // assistant turn is the reliable boundary across both old and new rounds.
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === 'assistant') {
        firstFocusedMessage = index;
        break;
      }
    }
  }
  const hasEarlierConversation = firstFocusedMessage > 0;
  const visibleMessages = hasEarlierConversation && !showEarlier
    ? messages.slice(firstFocusedMessage)
    : messages;

  return (
    <div className={cn('space-y-8', className)}>
      {hasEarlierConversation && (
        <button
          type="button"
          onClick={() => setShowEarlier((current) => !current)}
          aria-expanded={showEarlier}
          className="-my-[7px] -ml-2 inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 text-[11px] text-how-text-on-paper/40 transition-colors hover:text-how-space-deep/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-how-space-deep/30"
        >
          {showEarlier ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {showEarlier ? 'Hide earlier messages' : 'Earlier in this round'}
        </button>
      )}

      {visibleMessages.map((msg, index) => {
        const content = msg.role === 'assistant' ? prepareHowieMarkdown(msg.content) : msg.content;
        if (msg.role === 'assistant' && !content) return null;

        return (
          <div
            key={msg.id ?? `${msg.timestamp}-${index}`}
            className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
          >
            <div
              className={
                msg.role === 'user'
                  ? 'max-w-[88%] rounded-2xl rounded-br-md border border-how-space-deep/[0.04] bg-how-rose/35 px-4 py-3 sm:max-w-[78%] sm:px-5'
                  : 'w-full max-w-[680px] border-l border-how-space-deep/10 py-1 pl-5 sm:pl-6'
              }
            >
              {msg.role === 'assistant' ? (
                <div className="prose prose-sm max-w-none prose-dark prose-chat prose-p:leading-[1.65]">
                  {renderHowieMarkdown(content)}
                </div>
              ) : (
                <p className="font-body text-[15px] leading-6 text-how-text-on-paper sm:text-[16px] sm:leading-[26px]">
                  {content}
                </p>
              )}
            </div>
          </div>
        );
      })}

      {isStreaming && streamingText && (
        <div className="flex justify-start">
          <div className="w-full max-w-[680px] border-l border-how-space-deep/10 py-1 pl-5 sm:pl-6">
            <div className="prose prose-sm max-w-none prose-dark prose-chat prose-p:leading-[1.65]">
              {renderHowieMarkdown(streamingText)}
            </div>
          </div>
        </div>
      )}

      {isSending && !isStreaming && (
        <div className="flex justify-start">
          <div
            role="status"
            className="flex items-center gap-2.5 border-l border-how-space-deep/10 py-2 pl-5 text-sm text-how-text-secondary/70 sm:pl-6"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-how-accent-warm" aria-hidden="true" />
            <span>Considering&hellip;</span>
          </div>
        </div>
      )}

      {footer}

      {error && (
        <div className="flex justify-start">
          <div className="w-full max-w-[680px] border-l border-red-400/50 py-1 pl-5 text-sm leading-6 text-red-500 sm:pl-6">
            {error}
          </div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}

export default UnitChat;
